import { Button, DateRangePicker } from 'vnetwork-ui'
import { Dialog } from 'vnetwork-ui'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import dayjs, { Dayjs } from 'dayjs'
import toast from 'react-hot-toast'
import { autoSyncWorkday } from '@/modules/WorkDay/services'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface AutoSyncDialogProps {
    open: boolean
    onClose: () => void
    refetchData: () => void
}

const AutoSyncDialog = ({
    open,
    onClose,
    refetchData,
}: AutoSyncDialogProps) => {
    const { t } = useTranslation('workday')
    const [value, setValue] = useState<
        [] | [Dayjs | undefined, Dayjs | undefined]
    >([])

    const { mutateAsync: sync, isPending } = useMutationWithHandler(
        {
            mutationFn: autoSyncWorkday,
        },
        {
            reactQueryKeys: [
                QUERY_KEYS.WORKDAYS,
                QUERY_KEYS.ATTENDANCE_SYNC_LOGS,
            ],
            onSuccess: (data: any) => {
                const result = data.result
                toast.success(
                    `${t('sync_completed')}: ${result.success_count} ${t('success')}, ${result.fail_count} ${t('failed')}`
                )
                onClose()
                refetchData()
            },
        }
    )

    const handleSync = async () => {
        if (!value[0] || !value[1]) {
            toast.error(t('please_select_date_range'))
            return
        }

        const fromDate = dayjs(value[0]).format('YYYY-MM-DD')
        const toDate = dayjs(value[1]).format('YYYY-MM-DD')

        await sync({ from_date: fromDate, to_date: toDate })
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={t('auto-sync')}
            maxWidth="sm"
            actions={
                <div className="flex gap-2 justify-end">
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setValue([])
                            onClose()
                        }}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        onClick={handleSync}
                        loading={isPending}
                        disabled={!value[0] || !value[1]}
                    >
                        {t('sync')}
                    </Button>
                </div>
            }
        >
            <div className="py-4">
                <div className="mb-2 text-sm text-gray-500">
                    {t('select_date_range_to_sync')}
                </div>
                <DateRangePicker
                    values={value}
                    onChange={(values) => setValue(values)}
                    placeholder={t('select_date_range')}
                />
                <div className="mt-2 text-xs text-gray-400">
                    {t('max_31_days')}
                </div>
            </div>
        </Dialog>
    )
}

export default AutoSyncDialog
